<!--
  The compact affordance. A `paper-button` rather than a bare element so the
  hover wash, the pressed state, the focus ring and the reduced-motion
  handling are the design system's; only the pill's own metrics and its
  severity paint are overridden, through the library's `--im-button-*` seam.

  `data-pop-trigger` is required by the popover band: without it the
  capture-phase click-away closes on pointerdown and the following click
  reopens, so the popover appears never to close.
-->
@if (segments().length > 0) {
  <paper-button
    cdkOverlayOrigin
    #origin="cdkOverlayOrigin"
    [class]="'beacon beacon--' + tone()"
    [class.beacon--blocking]="blocking() > 0"
    [class.beacon--open]="open()"
    variant="outline"
    tone="neutral"
    size="sm"
    shape="pill"
    [data-testid]="'alert-beacon'"
    data-pop-trigger="1"
    [ariaExpanded]="open()"
    [ariaControls]="panelId"
    ariaHaspopup="dialog"
    [ariaLabel]="(open()
      ? 'shell.alert_beacon.hide'
      : 'shell.alert_beacon.show') | transloco"
    (click)="toggle()"
  >
    <!--
      The plaque is HATCHED whenever something is closed — the estate-wide
      language for out-of-service (Manage hatches closed polygons; the
      Schedule rig hatches closed lanes). It is a second encoding of the
      same fact, not a replacement for the tone: strip the colour and the
      hatch still says the space is out of service.
    -->
    <span
      class="beacon__plaque"
      [class.beacon__plaque--hatch]="blocking() > 0"
      aria-hidden="true"
    >
      <paper-icon [name]="glyph()" [size]="14" />
    </span>

    @for (seg of segments(); track seg.key) {
      <!-- No separator glyph in the DOM: the gap between segments does the
           `·` reading, so screen readers get "2 closed, 1 urgent" rather
           than a run of middots. -->
      <span [class]="'beacon__seg beacon__seg--' + seg.key">
        <span class="beacon__n">{{ seg.count }}</span>
        <span class="beacon__word">{{ seg.labelKey | transloco }}</span>
      </span>
    }

    @if (blocking() > 0) {
      <!-- Stated in words, not implied by a colour. Hidden below 768px in
           SCSS, where the pill has to stay narrower than the canvas it is
           floating over — the error tone, the hatch and the "N closed"
           segment carry it there. -->
      <paper-tag class="beacon__blocked" intent="error" size="xs">
        {{ 'shell.alert_beacon.blocked' | transloco }}
      </paper-tag>
    }

    <paper-icon class="beacon__chevron" name="chevron-down" [size]="13" />
  </paper-button>

  <!-- Announced once, to assistive tech, independently of the popover.
       Compact must not mean silent. -->
  <span class="sr-only" role="status">
    {{ 'shell.alert_beacon.status' | transloco:{
      count: alerts().length, blocked: blocking() } }}
  </span>

  <!--
    PORTALLED OUT, deliberately. The pill carries `backdrop-filter`, and a
    `backdrop-filter` element becomes the containing block for its
    `position: fixed` descendants — a popover nested inside it would anchor
    to the pill's box instead of the viewport and be clipped by the map's
    floating chrome. The CDK pane lives in `.cdk-overlay-container` on
    `<body>`, so `position: fixed` means what it says.

    The pane does not POSITION anything: `anchorFrom` measures the box (the
    app's one popover geometry, `core/overlays/anchor.util.ts`) and
    `data-popover` supplies the surface. CDK is here for the portal.
  -->
  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="origin"
    [cdkConnectedOverlayOpen]="open()"
    cdkConnectedOverlayPanelClass="alert-beacon-portal"
    (detach)="closeFromToggle()"
  >
    <div
      class="beacon__panel"
      [attr.data-popover]="popoverKey"
      [id]="panelId"
      role="dialog"
      [attr.aria-label]="'shell.alert_beacon.panel_label' | transloco"
      [style.top.px]="box()?.top"
      [style.left.px]="box()?.left"
      [style.width.px]="box()?.width"
      [style.max-height]="box()?.maxHeightCss"
    >
      <!--
        The SAME banner the band renders elsewhere, in its full (non-compact)
        form. The rows, the severity tags, the time windows, the CTA modes
        and the per-row dismiss are already right; what was wrong was where
        they were being drawn, not what they say.
      -->
      <paper-alerts-banner
        [alerts]="alerts()"
        context="building"
        [dismissible]="true"
        (dismiss)="dismissAlert.emit($event)"
        (ctaClick)="ctaClick.emit($event)"
      />
    </div>
  </ng-template>
}
